import {useCallback, useState} from 'react';
import {mod} from 'react-swipeable-views-core';

type SwipeableImagesApi = {
  activeSlideIndex: number;
  goBack: () => void;
  goForward: () => void;
  goToSlide: (slideIndex: number) => void;
  goToImage: (imageIndex: number) => void;
  getInternalProps: () => {
    slideIndex: number;
    imagesPerSlide: number;
    onChangeSlideIndex: (slideIndex: number) => void;
  };
};

type Config = {
  imageCount: number;
  imagesPerSlide: number;
  initialImageIndex?: number;
};

export const useSwipeableImages = ({
  imageCount,
  imagesPerSlide,
  initialImageIndex = 0,
}: Config): SwipeableImagesApi => {
  // note that `slideIndex` behaves more like an offset when in "carousel" mode,
  // which means it can "overflow" and also get negative.
  const [slideIndex, setSlideIndex] = useState(initialImageIndex);
  const slideCount = Math.ceil(imageCount / imagesPerSlide);
  const activeSlideIndex =
    imagesPerSlide === 1 ? mod(slideIndex, imageCount) : slideIndex;

  const goBack = useCallback(() => {
    setSlideIndex((previous) => previous - 1);
  }, []);

  const goForward = useCallback(() => {
    setSlideIndex((previous) => previous + 1);
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      setSlideIndex((previous) => {
        const delta = index - mod(previous, slideCount);

        return previous + delta;
      });
    },
    [slideCount]
  );

  const goToImage = useCallback(
    (imageIndex: number) => {
      goToSlide(
        imagesPerSlide === 1
          ? imageIndex
          : Math.floor(imageIndex / imagesPerSlide)
      );
    },
    [goToSlide, imagesPerSlide]
  );

  const onChangeSlideIndex = useCallback((index: number) => {
    setSlideIndex(index);
  }, []);

  const getInternalProps = useCallback(
    () => ({
      slideIndex,
      imagesPerSlide,
      onChangeSlideIndex,
    }),
    [slideIndex, imagesPerSlide, onChangeSlideIndex]
  );

  return {
    activeSlideIndex,
    goBack,
    goForward,
    goToSlide,
    goToImage,
    getInternalProps,
  };
};
